import { useTranslation } from 'react-i18next';

import { useConfig } from '@entities/config/api';
import { Skeleton } from '@shared/ui';

import { LegalAddress, LegalArticle } from './LegalArticle';

/**
 * Impressum — § 5 DDG (с 14.05.2024 вместо § 5 TMG).
 *
 * Реквизиты приходят из `GET /config`, а не лежат в локалях: расхождение между
 * Impressum, Widerrufsbelehrung и подтверждением договора — типовое основание
 * Abmahnung, а собираются эти три текста в разных местах.
 *
 * **Ссылки на OS-платформу ЕС здесь нет и не будет.** Платформа прекратила
 * работу 20.07.2025, и мёртвая ссылка сама по себе вводит в заблуждение.
 *
 * Что здесь **не** пишется: ни слова о проверке автомобилей, документов или
 * добросовестности продавца (MVP_PRD §2). Impressum описывает оператора, а не
 * рекламирует услугу.
 */
export function ImprintPage() {
  const { t } = useTranslation();
  const { data, isPending } = useConfig();

  if (isPending || !data) {
    return (
      <LegalArticle title="Impressum">
        <Skeleton height="16rem" radius="var(--radius-card)" />
      </LegalArticle>
    );
  }

  const operator = data.legal.operator;

  return (
    <LegalArticle title="Impressum" version={data.legal.document_versions.imprint}>
      <h2>Angaben gemäß § 5 DDG</h2>
      <LegalAddress
        lines={[
          operator.legal_name,
          operator.street,
          `${operator.postal_code} ${operator.city}`,
          operator.country,
        ]}
      />

      <p>
        Vertreten durch den Geschäftsführer: {operator.managing_director}
        <br />
        Eingetragen im {operator.register_name} ({operator.register_authority})
        <br />
        Registrierungsnummer: {operator.registration_number}
        <br />
        USt-IdNr.: {operator.vat_id}
      </p>

      <h2>Kontakt</h2>
      <p>
        E-Mail: <a href={`mailto:${operator.email}`}>{operator.email}</a>
        {operator.phone ? (
          <>
            <br />
            Telefon: <a href={`tel:${operator.phone.replace(/\s/g, '')}`}>{operator.phone}</a>
          </>
        ) : null}
        <br />
        Website: {operator.website}
      </p>

      <h2>CarViQ.DE</h2>
      <p>
        CarViQ.DE ist ein Dienst der {operator.legal_name}. CarViQ.DE hilft beim Auffinden und
        Vergleichen von Fahrzeugen aus dem eigenen CarViQ.DE-Katalog, stellt Autohäusern Fragen und
        gibt deren Antworten wörtlich wieder, verhandelt auf Wunsch über den Preis und begleitet bis
        zum Termin beim Autohaus.
      </p>
      <p>
        CarViQ.DE ist kein Fahrzeughändler und keine Vertragspartei eines Kaufvertrags zwischen
        Käufer und Verkäufer. Die Kaufentscheidung trifft ausschließlich die Kundin oder der Kunde.
        CarViQ.DE prüft weder Fahrzeuge noch Fahrzeughistorie, VIN, Unterlagen oder den technischen
        Zustand und bewertet keine Autohäuser.
      </p>

      <h2>Hinweis zu künstlicher Intelligenz</h2>
      <p>
        Der Assistent Karl ist ein KI-System im Sinne der Verordnung (EU) 2024/1689. Sie
        interagieren mit einer künstlichen Intelligenz und nicht mit einem Menschen. Angaben von
        Autohäusern werden wörtlich zitiert und von CarViQ.DE nicht überprüft.
      </p>

      <h2>Verbraucherstreitbeilegung</h2>
      <p>
        Die {operator.legal_name} ist weder verpflichtet noch bereit, an Streitbeilegungsverfahren
        vor einer Verbraucherschlichtungsstelle teilzunehmen, soweit keine gesetzliche Verpflichtung
        zur Teilnahme besteht.
      </p>

      <h2>Haftung für Inhalte und Links</h2>
      <p>
        Für eigene Inhalte auf diesen Seiten sind wir nach den allgemeinen Gesetzen verantwortlich.
        Angaben, die von Autohäusern stammen, geben wir unverändert wieder; für deren Richtigkeit
        übernehmen wir keine Gewähr und weisen bei jeder solchen Angabe auf ihre Herkunft hin. Für
        Inhalte externer Websites, auf die verwiesen wird, ist jeweils deren Anbieter
        verantwortlich.
      </p>

      <p>{t('legal.imprint.contactHint')}</p>
    </LegalArticle>
  );
}
